import React, { useState } from 'react';
import './DestinationsMapFixed.css';

// FIGMA MCP EXACT ASSET SPECIFICATIONS
// Localhost URLs for pixel-perfect development experience
const figmaAssets = {
  toggleSwitch: "/assets/images/icons/toggle-switch.svg",
  mapVector: "https://images.ctfassets.net/hz1mviiqoqof/3dFivxqBaYtJ0VDWRAipIw/bb41664a15e7f05f0984103fac38d71c/1dff4312b6b5c62c56957706fee205a3d701b6de.svg",
  popupArrow: "/assets/images/icons/popup-arrow.svg",
  santaCatarinaArrow: "/assets/images/icons/santa-catarina-arrow.svg",
  locationPointer: "/assets/images/icons/location-pointer.svg"
};

interface ToggleSwitchProps {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ label, isActive = false, onClick }) => {
  return (
    <div 
      className="map-fixed__switch"
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="map-fixed__switch-toggle">
        <img
          alt=""
          className="map-fixed__switch-image"
          loading="lazy"
          src={figmaAssets.toggleSwitch}
        />
      </div>
      <div className="map-fixed__switch-label">
        <p className="map-fixed__switch-text">{label}</p>
      </div>
    </div>
  );
};

interface PopupLocationProps {
  destination: 'atacama' | 'patagonia' | 'santa-catarina';
  position: 'left' | 'right';
}

const PopupLocation: React.FC<PopupLocationProps> = ({ destination, position }) => {
  const getDisplayName = () => {
    switch (destination) {
      case 'atacama': return 'Atacama';
      case 'patagonia': return 'Patagonia';
      case 'santa-catarina': return 'Santa Catarina';
      default: return destination;
    }
  };

  const arrowSrc = destination === 'santa-catarina' ? figmaAssets.santaCatarinaArrow : figmaAssets.popupArrow;
  const isRotated = destination === 'santa-catarina';

  return (
    <div className={`map-fixed__popup map-fixed__popup--${destination} map-fixed__popup--${position}`}>
      {position === 'left' && (
        <div className="map-fixed__popup-arrow-container">
          <div className={`map-fixed__popup-arrow ${isRotated ? 'map-fixed__popup-arrow--rotated' : ''}`}>
            <img
              alt=""
              className="map-fixed__popup-arrow-image"
              loading="lazy"
              src={arrowSrc}
            />
          </div>
        </div>
      )}
      
      <div className="map-fixed__popup-content">
        <div className="map-fixed__popup-name">
          <p className="map-fixed__popup-text">{getDisplayName()}</p>
        </div>
      </div>
      
      {position === 'right' && (
        <div className="map-fixed__popup-arrow-container">
          <div className="map-fixed__popup-arrow">
            <img
              alt=""
              className="map-fixed__popup-arrow-image"
              loading="lazy"
              src={figmaAssets.popupArrow}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const DestinationsMapFixed: React.FC = () => {
  const [showAirports, setShowAirports] = useState(false);
  const [showFlights, setShowFlights] = useState(false);

  return (
    <div className="map-fixed" data-name="Map NEW">
      <div className="map-fixed__instance">
        {/* Text Block + Button - Exact Figma positioning */}
        <div className="map-fixed__text-block">
          <div className="map-fixed__title">
            <p className="map-fixed__title-text">
              Combine 2 or 3 of our lodges for the perfect South American Adventure
            </p>
          </div>
          <div className="map-fixed__body">
            <div className="map-fixed__description">
              <p className="map-fixed__description-text">
                When combining our destinations, enjoy complimentary stays at our favourite hotels in connecting cities, plus enjoy local know-how from our experts who will help create your tailor-made experience.
              </p>
            </div>
            <div className="map-fixed__button">
              <div className="map-fixed__button-border" />
              <div className="map-fixed__button-content">
                <p className="map-fixed__button-text">PLAN YOUR TRIP</p>
              </div>
            </div>
          </div>
        </div>

        {/* Map - Exact Figma positioning and hierarchy */}
        <div className="map-fixed__map">
          {/* Map Toggles - Exact Figma positioning */}
          <div className="map-fixed__toggles">
            <ToggleSwitch 
              label="airports"
              isActive={showAirports}
              onClick={() => setShowAirports(!showAirports)}
            />
            <ToggleSwitch 
              label="flights & connections"
              isActive={showFlights}
              onClick={() => setShowFlights(!showFlights)}
            />
          </div>

          {/* Map Vector - Exact Figma positioning */}
          <div className="map-fixed__vector">
            <div className="map-fixed__vector-container">
              <img
                alt=""
                className="map-fixed__vector-image"
                loading="lazy"
                src={figmaAssets.mapVector}
              />
            </div>
          </div>

          {/* Destinations - Exact Figma positioning and hierarchy */}
          <div className="map-fixed__destinations">
            <div className="map-fixed__popup-locations">
              {/* Atacama - Exact Figma positioning */}
              <div className="map-fixed__location map-fixed__location--atacama">
                <PopupLocation destination="atacama" position="right" />
              </div>

              {/* Patagonia - Exact Figma positioning */}
              <div className="map-fixed__location map-fixed__location--patagonia">
                <PopupLocation destination="patagonia" position="right" />
              </div>

              {/* Santa Catarina - Exact Figma positioning */}
              <div className="map-fixed__location map-fixed__location--santa-catarina">
                <PopupLocation destination="santa-catarina" position="left" />
              </div>

              {/* Iguazu - Exact Figma positioning */}
              <div className="map-fixed__location map-fixed__location--iguazu">
                <div className="map-fixed__iguazu">
                  <div className="map-fixed__location-pointer">
                    <div className="map-fixed__location-pointer-container">
                      <img
                        alt=""
                        className="map-fixed__location-pointer-image"
                        loading="lazy"
                        src={figmaAssets.locationPointer}
                      />
                    </div>
                  </div>
                  <div className="map-fixed__iguazu-name">
                    <div className="map-fixed__iguazu-name-content">
                      <p className="map-fixed__iguazu-text">Iguazu</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationsMapFixed;